import { Injectable, InjectionToken, inject } from '@angular/core';

import { WINDOW_TOKEN } from '../../services/window.token';
import { ScriptLoaderAttributes, ScriptLoaderService } from '../../services/script-loader.service';
import { DataAnalyticsService } from '../../services/data-analytics/data-analytics.service';
import { DataAnalyticsEvent, DataAnalyticsInitialData } from '../../services/data-analytics/data-analytics.domain';

import { OneTrustService } from '../../services/one-trust/one-trust.service';
import { DataAnalyticsTaggingConstants } from './data-analytics-tagging.constants';
import { DataAnalyticsTaggingAuthenticationStatus, DataAnalyticsTaggingConfiguration, DataAnalyticsTaggingEvent } from './data-analytics-tagging.domain';

export const DATA_ANALYTICS_TAGGING_CONFIGURATION  = new InjectionToken<DataAnalyticsTaggingConfiguration>('dataAnalyticsTaggingConfiguration');

@Injectable({
  providedIn: 'root'
})
export class DataAnalyticsTaggingService {
  private readonly window = inject<Window>(WINDOW_TOKEN);
  private readonly oneTrustService = inject(OneTrustService);
  private readonly scriptLoaderService = inject(ScriptLoaderService);
  private readonly dataAnalyticsService = inject(DataAnalyticsService);
  private readonly dataAnalyticsTaggingConfig = inject<DataAnalyticsTaggingConfiguration>(DATA_ANALYTICS_TAGGING_CONFIGURATION, { optional: true });

  taggingEvents: Array<DataAnalyticsEvent> = [];
  private enabled: boolean = false;
  private context: string;

  constructor() {
    this.enabled = this.dataAnalyticsTaggingConfig?.dataLayerEnabled
  }

  init(context: string): void {
    this.context = context;
    if(this.enabled) {
      this.setDataAnalyticsTaggingScript();
      this.setDataAnalyticsTaggingData();
      this.registerDataAnalyticsEvents();
    }
  }

  emitTaggingEvent(event: DataAnalyticsTaggingEvent): void {
    if(event) {
      this.dataAnalyticsService.emitEvent(event)
    }
  }

  private getDataAnalyticsInitialData(): DataAnalyticsInitialData {
    return {
      baseEvent: {
        site: {
          environment: this.dataAnalyticsTaggingConfig?.environment,
          version: this.dataAnalyticsTaggingConfig?.buildTag,
          language: this.dataAnalyticsTaggingConfig?.locale
        },
        user: { authenticationStatus: DataAnalyticsTaggingAuthenticationStatus.AUTHENTICATED }
      },
      modalCloseEvent: DataAnalyticsTaggingConstants.defaultModalCloseEvent,
      routeStateEventMapper: this.dataAnalyticsTaggingConfig.routeStateTaggingEventMapper,
      eventNamePrefix: this.context
    }
  }

  private registerDataAnalyticsEvents(): void {
    this.dataAnalyticsService.init(this.getDataAnalyticsInitialData());
    this.dataAnalyticsService.dataAnalyticsEvent.subscribe((event: DataAnalyticsEvent) => {
      this.taggingEvents.push(event);
    });
  }

  private setDataAnalyticsTaggingScript(): void {
    this.oneTrustService.loadOptanonWrapper(
      DataAnalyticsTaggingConstants.getOptanonWrapperContent(this.dataAnalyticsTaggingConfig?.dataLayerScriptUrl)
    );
  }

  private setDataAnalyticsTaggingData(): void {
    const scriptAttrs: ScriptLoaderAttributes = {
      innerText: DataAnalyticsTaggingConstants.dataInitializationScriptContent,
      dataset: { adobeClientdataLayers: 'tms_tag' }
    }
    this.scriptLoaderService.loadScript(scriptAttrs);

    this.taggingEvents = this.window['tms_tag'] || this.taggingEvents ;
  }
}
